import {
  Button,
  Card,
  CardActionArea,
  CardContent,
  CircularProgress,
  Typography,
} from "@mui/material";
import { Business } from "@mui/icons-material";
import { useMutation } from "@tanstack/react-query";
import axios from "axios";
import { API_URL } from "const/constants";
import { finishAuthenticatedLogin } from "utils/postLoginNavigation";
import useKothar from "context/useKothar";
import { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

export default function SelectOrganization() {
  const navigate = useNavigate();
  const location = useLocation();
  const [, { setToken }] = useKothar();

  const memberships = useMemo(() => {
    const fromState = location.state?.memberships;
    if (Array.isArray(fromState) && fromState.length > 0) {
      return fromState;
    }
    try {
      const raw = sessionStorage.getItem("pendingOrgSelection");
      return raw ? JSON.parse(raw)?.memberships || [] : [];
    } catch {
      return [];
    }
  }, [location.state?.memberships]);

  const token = useMemo(() => {
    return (
      location.state?.token ||
      sessionStorage.getItem("pendingOrgSelectToken") ||
      localStorage.getItem("token")
    );
  }, [location.state?.token]);

  const requiresPasswordChange = useMemo(() => {
    if (location.state?.requiresPasswordChange) {
      return true;
    }
    try {
      const raw = sessionStorage.getItem("pendingOrgSelection");
      return raw ? Boolean(JSON.parse(raw)?.requiresPasswordChange) : false;
    } catch {
      return false;
    }
  }, [location.state?.requiresPasswordChange]);

  const { mutate, isLoading, variables } = useMutation({
    mutationFn: async (orgId) => {
      const resp = await axios.post(
        `${API_URL}/auth/select-org`,
        { orgId },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return resp.data;
    },
    onSuccess: (responseData) => {
      sessionStorage.removeItem("pendingOrgSelection");
      sessionStorage.removeItem("pendingOrgSelectToken");
      finishAuthenticatedLogin(responseData, {
        navigate,
        setToken,
        email: responseData?.email,
        successMessage: "Organization selected",
        requiresPasswordChange,
      });
    },
    onError: (err) => {
      const message = err?.response?.data?.errorMessage || "Could not select organization";
      toast.error(message);
    },
  });

  if (!memberships.length) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-app-surface p-6">
        <Card className="max-w-md w-full p-6 text-center">
          <Typography variant="h6" gutterBottom>
            No organizations available
          </Typography>
          <Button variant="contained" onClick={() => navigate("/login")} sx={{ mt: 2 }}>
            Back to login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 p-6">
      <div className="w-full max-w-2xl">
        <Typography variant="h4" className="text-white text-center mb-2 font-bold">
          Select organization
        </Typography>
        <Typography variant="body1" className="text-slate-300 text-center mb-8">
          Choose which consultancy you want to work in for this session.
        </Typography>
        <div className="grid gap-4">
          {memberships.map((membership) => (
            <Card key={membership.membershipId || membership.orgId} variant="outlined">
              <CardActionArea
                disabled={isLoading || membership.organizationDeleted}
                onClick={() => mutate(membership.orgId)}
              >
                <CardContent className="flex items-center gap-4">
                  <Business color="primary" />
                  <div className="flex-1">
                    <Typography variant="h6">{membership.orgName}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Role: {membership.role}
                      {membership.organizationDeleted ? " (deleted)" : ""}
                    </Typography>
                  </div>
                  {isLoading && variables === membership.orgId && <CircularProgress size={24} />}
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
